import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, size = 'md', label, ...props }, ref) => {
    const sizes = {
      sm: 'h-4 w-4',
      md: 'h-6 w-6',
      lg: 'h-8 w-8',
    };

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        className={cn('flex items-center justify-center gap-2', className)}
        {...props}
      >
        <Loader2 className={cn('animate-spin text-primary-500', sizes[size])} aria-hidden="true" />
        {label
          ? <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
          : <span className="sr-only">Loading</span>}
      </div>
    );
  }
);

Spinner.displayName = 'Spinner';

export interface PageSpinnerProps {
  label?: string;
  /**
   * Hold the spinner back this long. A page that arrives sooner never flashes
   * one — the space is still reserved, so nothing jumps when it does appear.
   */
  delayMs?: number;
  /** Fill the viewport, for a page with no app frame around it. */
  fullScreen?: boolean;
}

// Full page loading spinner
export function PageSpinner({ label = 'Loading...', delayMs = 0, fullScreen = false }: PageSpinnerProps) {
  const [visible, setVisible] = useState(delayMs <= 0);

  useEffect(() => {
    if (delayMs <= 0) return;
    const timer = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);

  return (
    <div className={cn('flex items-center justify-center', fullScreen ? 'min-h-screen' : 'min-h-[400px]')}>
      {visible && <Spinner size="lg" label={label} />}
    </div>
  );
}

export { Spinner };
